/**
 * Zensil — Luxury Animation System
 * Production-grade interactions: reveal, parallax, cursor, stagger
 */

(function () {
  'use strict';

  const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const isFinePointer = window.matchMedia('(any-pointer: fine)').matches;
  const isTouchDevice = 'ontouchstart' in window && navigator.maxTouchPoints > 0;

  /* ═══════════════════════════════════════════════════════
     1. SCROLL PROGRESS BAR
  ═══════════════════════════════════════════════════════ */
  function initScrollProgress() {
    const bar = document.createElement('div');
    bar.id = 'lux-scroll-progress';
    document.body.prepend(bar);

    let ticking = false;
    window.addEventListener('scroll', function () {
      if (!ticking) {
        requestAnimationFrame(function () {
          const scrollTop = window.scrollY;
          const docH = document.documentElement.scrollHeight - window.innerHeight;
          bar.style.width = (docH > 0 ? (scrollTop / docH) * 100 : 0) + '%';
          ticking = false;
        });
        ticking = true;
      }
    }, { passive: true });
  }

  /* ═══════════════════════════════════════════════════════
     2. INTERSECTION OBSERVER — SCROLL REVEALS
  ═══════════════════════════════════════════════════════ */
  function initScrollReveal() {
    if (prefersReducedMotion) return;

    const revealMap = [
      /* { selector, className, stagger } */
      { sel: '.collection-card',                  cls: 'lux-reveal-scale', stagger: true  },
      { sel: '.product-card',                     cls: 'lux-reveal',       stagger: true  },
      { sel: '[class*="cumin-video-card"]',        cls: 'lux-reveal-scale', stagger: true  },
    ];

    const observerOpts = {
      rootMargin: '0px 0px -72px 0px',
      threshold: 0.08
    };

    const observer = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (!entry.isIntersecting) return;
        const el = entry.target;
        const delay = parseFloat(el.dataset.luxDelay || 0);
        setTimeout(function () {
          el.classList.add('is-visible');
        }, delay * 1000);
        observer.unobserve(el);
      });
    }, observerOpts);

    revealMap.forEach(function (item) {
      const elements = Array.from(document.querySelectorAll(item.sel));

      /* Group siblings for staggering */
      if (item.stagger) {
        const groups = new Map();
        elements.forEach(function (el) {
          const parent = el.parentElement;
          if (!groups.has(parent)) groups.set(parent, []);
          groups.get(parent).push(el);
        });

        groups.forEach(function (siblings) {
          siblings.forEach(function (el, i) {
            el.classList.add(item.cls);
            el.dataset.luxDelay = (i * 0.09).toFixed(2);
            observer.observe(el);
          });
        });
      } else {
        elements.forEach(function (el) {
          el.classList.add(item.cls);
          observer.observe(el);
        });
      }
    });
  }

  /* ═══════════════════════════════════════════════════════
     3. SECTION HEADING — WORD-BY-WORD REVEAL
  ═══════════════════════════════════════════════════════ */
  function initHeadingWordReveal() {
    if (prefersReducedMotion) return;

    /* Target h2 and h3 that are NOT inside the hero (hero has CSS animations) */
    const headings = document.querySelectorAll(
      '.shopify-section h2:not(.hero-wrapper *), .shopify-section h3:not(.hero-wrapper *)'
    );

    const wordObserver = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (!entry.isIntersecting) return;
        const clips = entry.target.querySelectorAll('.lux-word-clip');
        clips.forEach(function (clip, i) {
          setTimeout(function () {
            clip.classList.add('is-visible');
          }, i * 75);
        });
        wordObserver.unobserve(entry.target);
      });
    }, { threshold: 0.25, rootMargin: '0px 0px -40px 0px' });

    headings.forEach(function (heading) {
      /* Only process headings with pure text content (no child elements) */
      if (heading.children.length > 0) return;
      const rawText = heading.textContent.trim();
      if (!rawText || rawText.split(' ').length < 2) return;

      /* Split into word spans */
      const words = rawText.split(/\s+/);
      heading.innerHTML = words.map(function (word, i) {
        return (
          '<span class="lux-word-clip" style="transition-delay:' + (i * 0.075) + 's">' +
            '<span class="lux-word-inner">' + word + '</span>' +
          '</span>' +
          (i < words.length - 1 ? ' ' : '')
        );
      }).join('');

      wordObserver.observe(heading);
    });
  }

  /* ═══════════════════════════════════════════════════════
     4. HERO PARALLAX
  ═══════════════════════════════════════════════════════ */
  function initHeroParallax() {
    if (prefersReducedMotion || isTouchDevice) return;

    const heroMedia = document.querySelector('.hero__media-grid');
    if (!heroMedia) return;

    const heroWrapper = document.querySelector('.hero-wrapper');
    if (!heroWrapper) return;

    let ticking = false;
    window.addEventListener('scroll', function () {
      if (!ticking) {
        requestAnimationFrame(function () {
          const scrollY = window.scrollY;
          const heroH = heroWrapper.offsetHeight;
          if (scrollY <= heroH + 80) {
            heroMedia.style.transform = 'translateY(' + (scrollY * 0.28).toFixed(2) + 'px)';
            heroMedia.style.willChange = 'transform';
          }
          ticking = false;
        });
        ticking = true;
      }
    }, { passive: true });
  }

  /* ═══════════════════════════════════════════════════════
     5. LUXURY CURSOR
  ═══════════════════════════════════════════════════════ */
  function initLuxuryCursor() {
    if (!isFinePointer || isTouchDevice || prefersReducedMotion) return;

    const dot  = document.createElement('div');
    const ring = document.createElement('div');
    dot.id  = 'lux-cursor';
    ring.id = 'lux-cursor-ring';
    document.body.append(dot, ring);

    let mouseX = window.innerWidth  / 2;
    let mouseY = window.innerHeight / 2;
    let ringX  = mouseX;
    let ringY  = mouseY;
    let rafId  = null;
    let hidden = false;

    document.addEventListener('mousemove', function (e) {
      mouseX = e.clientX;
      mouseY = e.clientY;
      dot.style.left = mouseX + 'px';
      dot.style.top  = mouseY + 'px';
      if (hidden) {
        dot.style.opacity  = '1';
        ring.style.opacity = '1';
        hidden = false;
      }
    }, { passive: true });

    document.addEventListener('mouseleave', function () {
      dot.style.opacity  = '0';
      ring.style.opacity = '0';
      hidden = true;
    });

    /* Smooth lagging ring */
    function animateRing() {
      ringX += (mouseX - ringX) * 0.11;
      ringY += (mouseY - ringY) * 0.11;
      ring.style.left = ringX + 'px';
      ring.style.top  = ringY + 'px';
      rafId = requestAnimationFrame(animateRing);
    }
    animateRing();

    /* Hover expansion on interactive elements */
    const hoverTargets = 'a, button, .product-card, .collection-card, [class*="cumin-video-card"], label, input, select';

    function onEnter() {
      dot.classList.add('is-hovered');
      ring.classList.add('is-hovered');
    }
    function onLeave() {
      dot.classList.remove('is-hovered');
      ring.classList.remove('is-hovered');
    }

    /* Delegate hover via document */
    document.addEventListener('mouseover', function (e) {
      if (e.target.closest(hoverTargets)) onEnter();
    });
    document.addEventListener('mouseout', function (e) {
      if (e.target.closest(hoverTargets) && !e.relatedTarget?.closest(hoverTargets)) onLeave();
    });
  }

  /* ═══════════════════════════════════════════════════════
     6. MAGNETIC CARD HOVER (subtle pull toward cursor)
  ═══════════════════════════════════════════════════════ */
  function initMagneticCards() {
    if (!isFinePointer || isTouchDevice || prefersReducedMotion) return;

    const cards = document.querySelectorAll('.collection-card');
    const STRENGTH = 0.025;

    cards.forEach(function (card) {
      card.addEventListener('mousemove', function (e) {
        const rect = card.getBoundingClientRect();
        const cx = e.clientX - rect.left - rect.width  / 2;
        const cy = e.clientY - rect.top  - rect.height / 2;
        card.style.transform = [
          'translate(' + (cx * STRENGTH).toFixed(2) + 'px,',
          (cy * STRENGTH).toFixed(2) + 'px)',
          'translateY(-7px)'
        ].join(' ');
      });

      card.addEventListener('mouseleave', function () {
        card.style.transition = 'transform 0.65s cubic-bezier(0.25, 0.46, 0.45, 0.94)';
        card.style.transform  = '';
        setTimeout(function () { card.style.transition = ''; }, 700);
      });
    });
  }

  /* ═══════════════════════════════════════════════════════
     7. STAGGER — PRODUCT GRID ON SECTION ENTER
  ═══════════════════════════════════════════════════════ */
  function initGridStagger() {
    if (prefersReducedMotion) return;

    /* Product grid containers */
    const grids = document.querySelectorAll('.resource-list, [class*="product-grid"], [class*="collection-grid"]');
    grids.forEach(function (grid) {
      grid.setAttribute('data-lux-stagger', '');
    });
  }

  /* ═══════════════════════════════════════════════════════
     8. VIDEO SECTION — PAUSE / PLAY ON VIEWPORT
  ═══════════════════════════════════════════════════════ */
  function initVideoAutoplay() {
    const videoCards = document.querySelectorAll('[class*="cumin-video-card"]');

    const vidObserver = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        const video = entry.target.querySelector('video');
        if (!video) return;
        if (entry.isIntersecting) {
          video.play().catch(function () {});
        } else {
          video.pause();
        }
      });
    }, { threshold: 0.3 });

    videoCards.forEach(function (card) { vidObserver.observe(card); });
  }

  /* ═══════════════════════════════════════════════════════
     9. SMOOTH SECTION TRANSITIONS — subtle fade between sections
  ═══════════════════════════════════════════════════════ */
  function initSectionFades() {
    if (prefersReducedMotion) return;

    const sections = document.querySelectorAll('.shopify-section');
    const secObserver = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (entry.isIntersecting) {
          entry.target.style.opacity = '1';
        }
      });
    }, { threshold: 0.04 });

    sections.forEach(function (sec, i) {
      /* Skip the first section (hero — already animated) */
      if (i === 0) return;
      sec.style.transition = 'opacity 0.7s cubic-bezier(0.16, 1, 0.3, 1) 0.1s';
      sec.style.opacity = '0';
      secObserver.observe(sec);
    });
  }

  /* ═══════════════════════════════════════════════════════
     10. INIT
  ═══════════════════════════════════════════════════════ */
  function init() {
    initScrollProgress();
    initScrollReveal();
    initHeadingWordReveal();
    initHeroParallax();
    initGridStagger();
    initVideoAutoplay();
    initSectionFades();

    /* Cursor & magnetic — defer slightly to not block paint */
    setTimeout(function () {
      initLuxuryCursor();
      initMagneticCards();
    }, 300);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  /* Re-init after Shopify section re-renders (theme editor) */
  document.addEventListener('shopify:section:load', init);

})();
